export enum UsersRoleOptions {
    Admin = "admin",
    Member = "member"
}

export interface UsersExpanded {
    org?: Organizations;
    [key: string]: unknown;
}

export interface Users {
    expand?: UsersExpanded;
    id: string;
    email: string;
    emailVisibility: boolean;
    verified: boolean;
    name: string;
    avatar: string;
    org: string;
    isAdmin: boolean;
    role: UsersRoleOptions;
    utm_source: string;
    utm_medium: string;
    utm_campaign: string;
    utm_content: string;
    utm_term: string;
    created: string;
    updated: string;
}

export enum BrandsTypeOptions {
    Wordpress = "wordpress",
    Shopify = "shopify",
    Custom = "custom"
}

export enum BrandsStatusOptions {
    Active = "active",
    Archived = "archived"
}

export enum BrandsToneOptions {
    Friendly = "friendly",
    Professional = "professional",
    Conversational = "conversational",
    Authoritative = "authoritative"
}

export enum BrandsSyncStatusOptions {
    Pending = "pending",
    InProgress = "in_progress",
    Completed = "completed",
    Failed = "failed"
}

export enum BrandsVisualStyleOptions {
    Photorealistic = "photorealistic",
    FlatIllustration = "flat_illustration",
    MinimalAbstract = "minimal_abstract",
    Watercolour = "watercolour",
    HandDrawnSketch = "hand_drawn_sketch",
    VectorArt = "vector_art",
    CutoutCollage = "cutout_collage",
    PastelModern = "pastel_modern",
    DarkUiMode = "dark_ui_mode",
    ThreedRendered = "threed_rendered"
}

export interface BrandsExpanded {
    org?: Organizations;
    [key: string]: unknown;
}

export interface Brands {
    expand?: BrandsExpanded;
    id: string;
    name: string;
    url: string;
    description: string;
    type: BrandsTypeOptions;
    status: BrandsStatusOptions;
    logo: string;
    primaryColor: string;
    secondaryColor: string;
    tone: BrandsToneOptions;
    targetAudience: string;
    uniqueValue: string;
    additionalNotes: string;
    analyticsId: string;
    analyticsViewId: string;
    searchConsoleVerified: boolean;
    lastSyncAt: string;
    syncStatus: BrandsSyncStatusOptions;
    org: string;
    contentObjectives: object | null | "";
    domain: string;
    products: object | null | "";
    competitors: object | null | "";
    visualStyle: BrandsVisualStyleOptions;
    location: string;
    language: string;
    imageStyle: object | null | "";
    created: string;
    updated: string;
}

export enum CategoriesSourceOptions {
    imported = "'imported'",
    suggested = "'suggested'",
    manual = "'manual'"
}

export enum CategoriesStatusOptions {
    Active = "active",
    Archived = "archived"
}

export interface CategoriesExpanded {
    brand?: Brands;
    [key: string]: unknown;
}

export interface Categories {
    expand?: CategoriesExpanded;
    id: string;
    brand: string;
    name: string;
    description: string;
    slug: string;
    source: CategoriesSourceOptions;
    status: CategoriesStatusOptions;
    remoteCategoryId: string;
    seoTitle: string;
    seoDescription: string;
    lastSyncAt: string;
    created: string;
    updated: string;
}

export interface Organizations {
    expand?: { [key: string]: unknown; };
    id: string;
    name: string;
    domain: string;
    website: string;
    settings: object | null | "";
    created: string;
    updated: string;
}

export enum PostsStatusOptions {
    Draft = "draft",
    Published = "published",
    Archived = "archived",
    Failed = "failed",
    Generating = "generating",
    Scheduled = "scheduled"
}

export interface PostsExpanded {
    brand?: Brands;
    category?: Categories;
    [key: string]: unknown;
}

export interface Posts {
    expand?: PostsExpanded;
    id: string;
    brand: string;
    category: string;
    title: string;
    slug: string;
    status: PostsStatusOptions;
    seoTitle: string;
    seoDescription: string;
    featuredImage: string;
    publishedAt: string;
    keyword: string;
    publishedId: string;
    publishedUrl: string;
    regenerationCount: number;
    generationError: string;
    progress: object | null | "";
    created: string;
    updated: string;
    grade: string;
    gradeScore: number;
    gradeBreakdown: object | null | "";
    scheduleDate?: string;
}

export enum SubscriptionsStatusOptions {
    Active = "active",
    OnHold = "on_hold",
    Cancelled = "cancelled",
    Expired = "expired",
    Pending = "pending",
    Trialing = "trialing"
}

export enum SubscriptionsPlanOptions {
    Free = "free",
    Junior = "junior",
    Senior = "senior",
    Intern = "intern"
}

export enum SubscriptionsBillingTypeOptions {
    Monthly = "monthly",
    Annual = "annual"
}

export enum SubscriptionsWebhookVerifiedOptions {
    Pending = "pending",
    Verified = "verified",
    Failed = "failed"
}

export enum SubscriptionsExpectationsMetOptions {
    NotAtAll = "not_at_all",
    Somewhat = "somewhat",
    Fully = "fully"
}

export interface SubscriptionsExpanded {
    org?: Organizations;
    price?: Price;
    product?: Product;
    [key: string]: unknown;
}

export interface Subscriptions {
    expand?: SubscriptionsExpanded;
    id: string;
    org: string;
    status: SubscriptionsStatusOptions;
    plan: SubscriptionsPlanOptions;
    billingType: SubscriptionsBillingTypeOptions;
    subscriptionId: string;
    webhookVerified: SubscriptionsWebhookVerifiedOptions;
    startDate: string;
    endDate: string;
    lastBillingDate: string;
    nextBillingDate: string;
    articlesUsedThisMonth: number;
    articlesQuota: number;
    lastUsageResetDate: string;
    totalArticlesCreated: number;
    pgCustomerId: string;
    price: string;
    product: string;
    trialEndDate: string;
    cancelEndDate: string;
    cancellationReasons: object | null | "";
    expectationsMet: SubscriptionsExpectationsMetOptions;
    cancellationFeedback: string;
    cancelledAt: string;
    cancelAtPeriodEnd: boolean;
    created: string;
    updated: string;
}

export enum LeadsStatusOptions {
    Pending = "pending",
    Completed = "completed",
    Failed = "failed"
}

export interface Leads {
    expand?: { [key: string]: unknown; };
    id: string;
    name: string;
    email: string;
    website: string;
    topic: string;
    srcIp: string;
    referrer: string;
    browser: string;
    device: string;
    domain: string;
    articleId: string;
    accessCode: string;
    expiryDate: string;
    status: LeadsStatusOptions;
    title: string;
    content: string;
    imageUrl: string;
    contentState: object | null | "";
    created: string;
    updated: string;
}

export enum ContentSchedulesStatusOptions {
    Scheduled = "scheduled",
    InProgress = "in_progress",
    Completed = "completed",
    Failed = "failed"
}

export interface ContentSchedulesExpanded {
    brand?: Brands;
    post?: Posts;
    org?: Organizations;
    [key: string]: unknown;
}

export interface ContentSchedules {
    expand?: ContentSchedulesExpanded;
    id: string;
    brand: string;
    topic: string;
    keyword: string;
    scheduleDate: string;
    post: string;
    status: ContentSchedulesStatusOptions;
    org: string;
    regenerationCount: number;
    error: string;
    created: string;
    updated: string;
}

export interface PostsStats {
    expand?: { [key: string]: unknown; };
    id: string;
    org: object | null | "";
    total_articles: number;
    published: object | null | "";
    draft: object | null | "";
    scheduled: object | null | "";
}

export enum IntegrationsCategoryOptions {
    Cms = "cms",
    Analytics = "analytics",
    Webmaster = "webmaster"
}

export enum IntegrationsTypeOptions {
    Wordpress = "wordpress",
    Shopify = "shopify",
    Webflow = "webflow",
    Wix = "wix",
    Ghost = "ghost",
    Astro = "astro",
    Sanity = "sanity",
    Custom = "custom",
    GoogleAnalytics = "google analytics",
    Plausible = "plausible",
    SearchConsole = "search console",
    BingWebmaster = "bing webmaster"
}

export enum IntegrationsStatusOptions {
    Active = "active",
    Inactive = "inactive",
    Error = "error"
}

export interface IntegrationsExpanded {
    brand?: Brands;
    [key: string]: unknown;
}

export interface Integrations {
    expand?: IntegrationsExpanded;
    id: string;
    brand: string;
    category: IntegrationsCategoryOptions;
    type: IntegrationsTypeOptions;
    credentials: object | null | "";
    settings: object | null | "";
    status: IntegrationsStatusOptions;
    created: string;
    updated: string;
}

export interface PostsDetailExpanded {
    post?: Posts;
    [key: string]: unknown;
}

export interface PostsDetail {
    expand?: PostsDetailExpanded;
    id: string;
    outline: object | null | "";
    contentState: object | null | "";
    content: string;
    post: string;
    htmlContent: string;
    created: string;
    updated: string;
}

export enum PaymentWebhookStatusOptions {
    Pending = "pending",
    Processed = "processed",
    Failed = "failed"
}

export enum PaymentWebhookSourceOptions {
    Dodo = "dodo",
    Stripe = "stripe"
}

export interface PaymentWebhook {
    expand?: { [key: string]: unknown; };
    id: string;
    eventType: string;
    payload: object | null | "";
    relatedId: string;
    status: PaymentWebhookStatusOptions;
    errorMessage: string;
    webhookId: string;
    source: PaymentWebhookSourceOptions;
    created: string;
    updated: string;
}

export enum AuditsStatusOptions {
    Pending = "pending",
    Processing = "processing",
    Completed = "completed",
    Error = "error"
}

export interface AuditsExpanded {
    org?: Organizations;
    user?: Users;
    [key: string]: unknown;
}

export interface Audits {
    expand?: AuditsExpanded;
    id: string;
    url: string;
    status: AuditsStatusOptions;
    org: string;
    user: string;
    overview: object | null | "";
    enhancements: object | null | "";
    pagespeed: object | null | "";
    created: string;
    updated: string;
}

export interface PriceExpanded {
    product?: Product;
    [key: string]: unknown;
}

export interface Price {
    expand?: PriceExpanded;
    id: string;
    price_id: string;
    product_id: string;
    active: boolean;
    description: string;
    currency: string;
    unit_amount: number;
    type: string;
    interval: string;
    interval_count: number;
    trial_period_days: number;
    metadata: object | null | "";
    product: string;
    created: string;
    updated: string;
}

export interface Product {
    expand?: { [key: string]: unknown; };
    id: string;
    product_id: string;
    active: boolean;
    name: string;
    description: string;
    image: string;
    metadata: object | null | "";
    product_order: number;
    marketing_features: object | null | "";
    created: string;
    updated: string;
}

export enum ImagePromptsTypeOptions {
    Cover = "cover",
    Sub = "sub"
}

export enum ImagePromptsProviderOptions {
    Openai = "openai",
    Gemini = "gemini"
}

export interface ImagePromptsExpanded {
    org?: Organizations;
    [key: string]: unknown;
}

export interface ImagePrompts {
    expand?: ImagePromptsExpanded;
    id: string;
    name: string;
    type: ImagePromptsTypeOptions;
    provider: ImagePromptsProviderOptions;
    org: string;
    system_default: boolean;
    active: boolean;
    template_content: string;
    created: string;
    updated: string;
}